import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  setDoc,
  doc,
  getDoc,
  serverTimestamp,
  Timestamp
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { getCompetitionDocRef } from "./utils/firebaseUtils";
import { onAuthStateChanged } from "firebase/auth";
import { useAdmin } from "./hooks/useAdmin";

function calculatePoints(place, participants) {
  const raw =
    321 * Math.exp(-0.077 * place) * (0.39 * Math.log(participants) + 0.13);
  return Math.round(raw);
}

function convertRawTextToJSON(rawText) {
  const lines = rawText.split("\n");
  const dancersByRound = {};
  const recalls = {};
  const starDancers = new Set();
  const rounds = ["Final", "Semi", "Quarter", "Eighth", "Sixteenth"];

  let competitionName = "";
  let discipline = "Latin";
  let dance = "";
  let category = "";
  let timestamp = "";
  let currentRound = "";
  let inSkatingSummary = false;
  let starCount = 0;

  for (let raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const compMatch = line.match(/^(.+)\s+\((\d{2})\.(\d{2})\.(\d{4})\)\s*-\s*(.+)$/);
    if (compMatch) {
      competitionName = `${compMatch[1].trim()} – ${compMatch[5].trim()}`;
      const [, , dd, mm, yyyy] = compMatch;
      timestamp = `${yyyy}-${mm}-${dd}T00:00:00Z`;
      continue;
    }

    function standardizeDiscipline(text) {
      text = text.toLowerCase().trim();
      if (text.includes('latin')) return 'Latin';
      if (text.includes('hip') && text.includes('hop')) return 'Hip Hop';
      if (text.includes('contemporary')) return 'Contemporary';
      if (text.includes('commercial')) return 'Commercial';
      return 'Latin'; // default
    }

    function standardizeDance(text) {
      text = text.toLowerCase().trim();
      if (text.includes('samba')) return 'Samba';
      if (text.includes('cha')) return 'ChaChaCha';
      if (text.includes('rumba')) return 'Rumba';
      if (text.includes('paso') || text.includes('doble')) return 'Pasodoble';
      if (text.includes('jive')) return 'Jive';
      if (text.includes('contemporary')) return 'Contemporary';
      if (text.includes('hip') && text.includes('hop')) return 'Hip Hop';
      if (text.includes('commercial')) return 'Commercial';
      // Return the original text if no match, but capitalize first letter
      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    function standardizeCategory(text) {
      text = text.toLowerCase().trim();
      let ageGroup = '';
      
      if (text.includes('juvenile')) {
        if (text.includes('juvenile 2') || text.includes('juvenile ii')) {
          ageGroup = 'Juvenile II';
        } else if (text.includes('juvenile 1') || text.includes('juvenile i')) {
          ageGroup = 'Juvenile I';
        } else {
          ageGroup = 'Juvenile I'; // default
        }
      } else if (text.includes('junior')) {
        if (text.includes('junior 2') || text.includes('junior ii')) {
          ageGroup = 'Junior II';
        } else if (text.includes('junior 1') || text.includes('junior i')) {
          ageGroup = 'Junior I';
        } else {
          ageGroup = 'Junior I'; // default
        }
      } else if (text.includes('youth')) {
        ageGroup = 'Youth';
      } else if (text.includes('adult')) {
        ageGroup = 'Adult';
      }
      
      return 'Solo ' + ageGroup;
    }

    // Try different patterns for parsing round information
    // Pattern 1: "Junior 1 (12-13) Solo Samba Final" or "Junior 1 (12-13) Solo Samba 1/2 Final"
    let roundMatch = line.match(
      /^Detailed marks report:.*?\d+\.\s*([^0-9]+\s*\d*(?:\s*\([^)]+\))?)\s+Solo\s+([A-Za-z ]+)\s*(1\/4|1\/2|1\/8|1\/16)?\s*Final/i
    );
    
    // Pattern 2: "Solo Junior 1 (12-13) Samba Final" (original format)
    if (!roundMatch) {
      roundMatch = line.match(
        /^Detailed marks report:.*?\d+\.\s*Solo\s+([^0-9]+\s*\d*(?:\s*\([^)]+\))?)\s+([A-Za-z ]+)\s*(?:\d+\s+Dance\s+)?(1\/4|1\/2|1\/8|1\/16)?\s*Final/i
      );
    }
    
    // Pattern 3: "Juvenile 1 (7-9) Solo Contemporary Dance" (new pattern for Contemporary and other dances)
    if (!roundMatch) {
      roundMatch = line.match(
        /^Detailed marks report:.*?\d+\.\s*([^0-9]+\s*\d*(?:\s*\([^)]+\))?)\s+Solo\s+([A-Za-z ]+(?:\s+Dance)?)\s*(1\/4|1\/2|1\/8|1\/16)?\s*$/i
      );
    }
    
    // Pattern 4: Legacy pattern
    if (!roundMatch) {
      roundMatch = line.match(
        /^Detailed marks report:.*?\.\s*(?:\d+\.)?\s*(?:1 Dance\s+)?([^0-9]+?(?:1|2|I|II)?)\s*(?:\([^)]+\))?\s+Solo\s+([A-Za-z ]+)\s*(1\/4|1\/2|1\/8|1\/16)?\s*Final/i
      );
    }
    
    if (roundMatch) {
      category = standardizeCategory(roundMatch[1].trim());
      dance = standardizeDance(roundMatch[2].trim());
      
      discipline = ['Samba', 'Cha Cha Cha', 'Rumba', 'Paso Doble', 'Jive'].includes(dance) ? 'Latin' 
                : ['Hip Hop'].includes(dance) ? 'Hip Hop'
                : ['Contemporary'].includes(dance) ? 'Contemporary'
                : ['Commercial'].includes(dance) ? 'Commercial'
                : 'Latin';
      
      const tag = roundMatch[3] || "";
      currentRound =
        tag.includes("1/4")
          ? "Quarter"
          : tag.includes("1/2")
          ? "Semi"
          : tag.includes("1/8")
          ? "Eighth"
          : tag.includes("1/16")
          ? "Sixteenth"
          : "Final";
      dancersByRound[currentRound] = new Map();
      recalls[currentRound] = new Set();
      inSkatingSummary = false;
      continue;
    }

    if (/Skating summary|Marks Summary/i.test(line)) {
      inSkatingSummary = true;
      continue;
    }

    const parts = line
      .split(/\s{2,}|\t+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length < 3) continue; // Need at least place, number, name

    // Skip header lines
    if (parts.includes("Place") || parts.includes("Name") || parts.includes("Represents") || parts.includes("Judge")) continue;

    // Try to extract name and studio based on the table format
    let nameField, studioField;
    
    // For Skating summary format: #, №, Name, Represents, S, Σ
    // For Marks Summary format: Place, #, Name, Represents, S, Recall
    if (parts.length >= 4) {
      // If we see a place range (like "1-2") or single number as first element
      if (/^\d+(-\d+)?$/.test(parts[0]) || /^\d+$/.test(parts[0])) {
        nameField = parts[2];   // Name is usually at index 2
        // Check if parts[3] is actually a studio or just another number/code
        // Studio should not be a single digit or obvious place number
        if (parts[3] && !(/^\d+$/.test(parts[3]) && parts[3].length <= 2)) {
          studioField = parts[3]; // Studio is usually at index 3
        } else {
          studioField = ""; // No valid studio found
        }
      } else {
        // Fallback to original logic
        nameField = parts.find((p, idx) => idx > 1 && p && p !== "-" && p !== "Name" && p !== "Represents");
        const potentialStudio = parts.find((p, idx) => idx > 2 && p && p !== "-" && p !== "Name" && p !== "Represents");
        // Only use as studio if it's not a single/double digit number
        if (potentialStudio && !(/^\d+$/.test(potentialStudio) && potentialStudio.length <= 2)) {
          studioField = potentialStudio;
        } else {
          studioField = "";
        }
      }
    } else if (parts.length >= 3) {
      // Handle cases where there are only 3 parts (place, number, name) but no studio
      if (/^\d+(-\d+)?$/.test(parts[0]) || /^\d+$/.test(parts[0])) {
        nameField = parts[2];   // Name is at index 2
        studioField = "";       // No studio provided
      }
    }
    
    // Only require nameField, studio can be empty
    if (!nameField) continue;

    const name = nameField.replace("*", "");
    const studio = studioField && studioField.trim() ? studioField.trim() : "No Studio";
    const key = `${name}||${studio}`;

    // Filter out junk entries - only check name requirements, allow "No Studio"
    if (
      !name ||
      name.length < 3 ||
      /^[A-Za-z]$/.test(name) ||
      /^\d+$/.test(name) ||
      /^x$/i.test(name) ||
      !currentRound // Skip if no valid round is detected
    ) continue;

    // Check for star dancers - star can be in the number field or name field
    if (parts[1] && parts[1].includes("*")) {
      starDancers.add(key);
      starCount++;
    } else if (nameField && nameField.includes("*")) {
      starDancers.add(key);
      starCount++;
    }

    if (inSkatingSummary) {
      let placeStr = parts[0];
      let placeNum;
      
      // Handle range formats like "1-2", "3-6", "7-9", etc.
      if (placeStr.includes("-")) {
        const [startPlace] = placeStr.split("-");
        placeNum = parseFloat(startPlace);
      } else {
        placeNum = parseFloat(placeStr);
      }
      
      if (
        currentRound !== "Final" &&
        starCount > 0 &&
        !starDancers.has(key)
      ) {
        placeNum += starCount;
      }
      // Ensure the round exists before trying to set data
      if (!dancersByRound[currentRound]) {
        dancersByRound[currentRound] = new Map();
      }
      dancersByRound[currentRound].set(key, {
        dancerName: name,
        studio,
        place: placeNum,
        priority: rounds.indexOf(currentRound)
      });
    } else {
      // Check if this line contains recall information (look for "V" in appropriate column)
      const recallIndex = parts.findIndex(p => p.toUpperCase() === "V");
      const recall = recallIndex !== -1;
      
      if (recall) {
        // Ensure the recalls set exists before trying to add to it
        if (!recalls[currentRound]) {
          recalls[currentRound] = new Set();
        }
        recalls[currentRound].add(key);
      } else {
        let placeStr = parts[0];
        let placeNum = parseFloat(placeStr);
        if (isNaN(placeNum) && placeStr.includes("-")) {
          placeNum = parseFloat(placeStr.split("-")[0]);
        }
        if (
          currentRound !== "Final" &&
          starCount > 0 &&
          !starDancers.has(key)
        ) {
          placeNum += starCount;
        }
        // Ensure the round exists before trying to set data
        if (!dancersByRound[currentRound]) {
          dancersByRound[currentRound] = new Map();
        }
        dancersByRound[currentRound].set(key, {
          dancerName: name,
          studio,
          place: placeNum,
          priority: rounds.indexOf(currentRound)
        });
      }
    }
  }

  // Process results to find the best placement for each dancer
  const bestResults = new Map();
  for (let round of rounds) {
    const roundData = dancersByRound[round];
    if (!roundData) continue;

    for (let [key, result] of roundData.entries()) {
      // Check if this dancer appeared in a later (better) round
      const higherRounds = rounds.slice(0, rounds.indexOf(round));
      const appearedLater = higherRounds.some(
        (r) => dancersByRound[r]?.has(key)
      );
      
      // Only use this result if the dancer didn't appear in a later round
      if (!appearedLater) {
        if (
          !bestResults.has(key) ||
          result.priority < bestResults.get(key).priority
        ) {
          bestResults.set(key, result);
        }
      }
    }
  }

  // Convert to array and sort by round priority first, then by place
  const allResults = Array.from(bestResults.values());
  allResults.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.place - b.place;
  });

  // Assign final consecutive places accounting for ties
  let nextPlace = 1;
  for (let i = 0; i < allResults.length; i++) {
    const current = allResults[i];
    
    // Find all dancers tied at the current place and priority
    const tiedDancers = allResults.filter(
      d => d.place === current.place && d.priority === current.priority
    );
    
    // Assign the same place to all tied dancers
    for (const dancer of tiedDancers) {
      dancer.finalPlace = nextPlace;
    }
    
    // Skip over tied dancers we just processed
    const tiedCount = tiedDancers.length;
    i += tiedCount - 1;
    
    // Next place should increment by the number of tied dancers
    nextPlace += tiedCount;
  }

  // Set final place for each dancer
  for (const result of allResults) {
    result.place = result.finalPlace;
    delete result.finalPlace;
    delete result.priority;
  }
  const validResults = allResults;
  const participantKeys = new Set(validResults.map((r) => `${r.dancerName}||${r.studio}`));

  // Generate a competition ID using timestamp, competition name, discipline, category, and dance
  const datePart = timestamp ? timestamp.slice(0, 10).replace(/-/g, "") : "00000000";
  const timePart = timestamp ? timestamp.slice(11, 19).replace(/:/g, "") : Date.now().toString().slice(-6);
  
  // Clean competition name by removing common unnecessary words
  let cleanedName = competitionName || "unnamed";
  const wordsToRemove = ["Saturday", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Solo", "Latin", "Standard", "Adult", "Junior", "Youth", "Juvenile"];
  wordsToRemove.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, "gi");
    cleanedName = cleanedName.replace(regex, "");
  });
  
  // Clean up extra spaces and convert to safe format
  const namePart = cleanedName
    .replace(/\s+/g, "_")           // Replace spaces with underscores
    .replace(/[^a-zA-Z0-9_]/g, "_") // Replace special chars with underscores
    .replace(/_+/g, "_")            // Replace multiple underscores with single
    .replace(/^_|_$/g, "")          // Remove leading/trailing underscores
    .substring(0, 30);              // Limit length
  
  const disciplinePart = discipline ? discipline.replace(/[^a-zA-Z0-9]/g, "_") : "unknown";
  const categoryPart = category ? category.replace(/[^a-zA-Z0-9]/g, "_") : "unknown";
  const dancePart = dance ? `_${dance.replace(/[^a-zA-Z0-9]/g, "_")}` : "";
  
  const id = `${datePart}_${timePart}_${namePart}_${disciplinePart}_${categoryPart}${dancePart}`;

  return {
    id,
    name: competitionName,
    discipline,
    dance,
    category,
    timestamp,
    participants: participantKeys.size,
    results: validResults
  };
}
export default function UploadCompetition() {
  const [jsonInput, setJsonInput] = useState("");
  const [rawText, setRawText] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [compName, setCompName] = useState("");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const { isAdmin, loading: adminLoading } = useAdmin();

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthLoading(false);
      if (currentUser) {
        console.log("✅ User authenticated:", currentUser.email);
      } else {
        console.log("❌ User not authenticated");
      }
    });

    return () => unsubscribe();
  }, []);

  // Show loading state while checking admin status
  if (adminLoading || authLoading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🩰 Upload Competition Results</h2>
        <p>Loading...</p>
      </div>
    );
  }

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🩰 Upload Competition Results</h2>
        <div style={{ 
          padding: "2rem", 
          border: "1px solid #f5c6cb", 
          borderRadius: "4px",
          backgroundColor: "#f8d7da",
          color: "#721c24",
          marginTop: "2rem"
        }}>
          <h3>🔒 Access Denied</h3>
          <p>You need administrator privileges to access this page.</p>
          <p>Please contact an administrator if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  const handleConvert = () => {
    try {
      const converted = convertRawTextToJSON(rawText);
      converted.name = compName || converted.name;
      
      // Enhanced debugging for conversion
      console.log("🔍 Conversion details:", {
        competitionName: converted.name,
        discipline: converted.discipline,
        category: converted.category,
        dance: converted.dance,
        participantCount: converted.participants,
        resultsCount: converted.results.length,
        sampleResults: converted.results.slice(0, 3)
      });
      
      setJsonInput(JSON.stringify(converted, null, 2));
      setUploadStatus(`✅ Converted ${converted.participants} dancers - Check console for details`);
    } catch (err) {
      console.error("❌ Conversion error:", err);
      setUploadStatus("❌ Conversion failed: " + err.message);
    }
  };

  const handleUpload = async () => {
    try {
      // Enhanced authentication check
      console.log("🔍 Auth state check - Current user:", auth.currentUser);
      console.log("🔍 Auth state check - User from state:", user);
      console.log("🔍 Auth state check - User UID:", auth.currentUser?.uid);
      console.log("🔍 Auth state check - User email:", auth.currentUser?.email);
      console.log("🔍 Auth state check - Auth token exists:", !!auth.currentUser?.accessToken);
      
      if (!auth.currentUser) {
        setUploadStatus("❌ Please log in first. Current auth state: " + (user ? "logged in" : "not logged in"));
        return;
      }

      // Wait a moment to ensure auth token is fresh
      console.log("✅ User authenticated, checking token freshness...");
      try {
        const token = await auth.currentUser.getIdToken(true); // Force refresh
        console.log("✅ Fresh auth token obtained, length:", token.length);
      } catch (tokenError) {
        console.error("❌ Token refresh failed:", tokenError);
        setUploadStatus("❌ Authentication token refresh failed. Please log out and log in again.");
        return;
      }

      console.log("✅ Proceeding with upload...");
      setUploadStatus("⏳ Starting upload...");
      
      const data = JSON.parse(jsonInput);
      if (!data.id || !data.timestamp) throw new Error("Missing ID or date");

      data.discipline = (data.discipline || "Latin").trim();
      data.category = (data.category || "").trim();
      data.dance = (data.dance || "").trim();
      
      // Auto-set dance field if missing based on discipline
      if (!data.dance) {
        if (data.discipline === "Contemporary") {
          data.dance = "Contemporary";
        } else if (data.discipline === "Hip Hop") {
          data.dance = "Hip Hop";
        } else if (data.discipline === "Commercial") {
          data.dance = "Commercial";
        }
        // For Latin, leave empty since it has multiple dances that should be specified
      }

      if (typeof data.timestamp === "string") {
        const d = new Date(data.timestamp);
        data.timestamp = Timestamp.fromDate(new Date(Date.UTC(
          d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0
        )));
      }

      data.results = Array.isArray(data.results)
        ? data.results.map((r) => ({
            ...r,
            points: calculatePoints(r.place, data.participants)
          }))
        : [];

      console.log("🔍 Competition data prepared:", {
        id: data.id,
        discipline: data.discipline,
        category: data.category,
        dance: data.dance,
        resultsCount: data.results.length,
        timestamp: data.timestamp,
        sampleDancers: data.results.slice(0, 3).map(r => r.dancerName)
      });

      setUploadStatus("⏳ Getting document reference...");

      // Save to the OLD structure (competitions collection) for immediate compatibility
      const docRef = doc(collection(db, "competitions"), data.id);
      console.log("🔍 Document reference path (OLD STRUCTURE):", docRef.path);
      
      setUploadStatus("⏳ Uploading competition data to old structure...");
      
      // Store in the appropriate structure
      const competitionDoc = {
        id: data.id,
        name: compName || data.name,
        discipline: data.discipline,
        dance: data.dance,
        category: data.category,
        timestamp: data.timestamp,
        uploadedAt: serverTimestamp(), // Track when competition was uploaded to the app
        multiplier: parseFloat(multiplier) || 1,
        participants: data.participants,
        results: data.results
      };
      
      console.log("🔍 About to upload document:", {
        path: docRef.path,
        docSize: JSON.stringify(competitionDoc).length,
        resultCount: competitionDoc.results.length
      });
      
      // Check for duplicates first
      setUploadStatus("⏳ Checking for duplicate competitions...");
      try {
        // Try to get the document first to check if it already exists
        console.log("🔍 Checking if competition already exists:", docRef.path);
        const existingDoc = await getDoc(docRef);
        
        if (existingDoc.exists()) {
          const existingData = existingDoc.data();
          console.log("⚠️ Competition already exists:", {
            id: data.id,
            existingName: existingData.name,
            existingTimestamp: existingData.timestamp,
            newName: competitionDoc.name,
            newTimestamp: competitionDoc.timestamp
          });
          
          // Show confirmation dialog for duplicate
          const shouldOverwrite = window.confirm(
            `A competition with this ID already exists:\n\n` +
            `Existing: "${existingData.name}" (${existingData.timestamp})\n` +
            `New: "${competitionDoc.name}" (${competitionDoc.timestamp})\n\n` +
            `Do you want to overwrite the existing competition?\n\n` +
            `Click OK to overwrite, Cancel to abort upload.`
          );
          
          if (!shouldOverwrite) {
            setUploadStatus("❌ Upload cancelled - duplicate competition detected");
            return;
          }
          
          console.log("✅ User confirmed overwrite of existing competition");
        } else {
          console.log("✅ No duplicate found - proceeding with upload");
        }
        
        // Test write permissions by doing the actual upload
        setUploadStatus("⏳ Uploading competition data...");
        console.log("🔍 Uploading competition data...");
        await setDoc(docRef, competitionDoc);
        console.log("✅ Competition uploaded successfully");
      } catch (permissionError) {
        console.error("❌ Permission test failed:", {
          message: permissionError.message,
          code: permissionError.code,
          path: docRef.path,
          userUid: auth.currentUser?.uid,
          userEmail: auth.currentUser?.email
        });
        
        if (permissionError.code === 'permission-denied') {
          setUploadStatus(`❌ Permission denied for path: ${docRef.path}. Check Firestore rules or try logging out and back in.`);
        } else {
          setUploadStatus(`❌ Upload failed: ${permissionError.message}`);
        }
        return;
      }
      
      console.log("✅ Competition data uploaded successfully to:", docRef.path);
      
      setUploadStatus("⏳ Creating competition log...");

      await addDoc(collection(db, "competitionLogs"), {
        uploadedBy: auth.currentUser?.email || "Unknown",
        timestamp: serverTimestamp(),
        summary: `${compName || data.name} (${data.participants} participants)`,
        documentPath: docRef.path
      });

      console.log("✅ Competition log created successfully");
      setUploadStatus(`✅ Upload successful! Document stored at: ${docRef.path}`);
      
    } catch (error) {
      console.error("❌ Upload error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setUploadStatus(`❌ Upload failed: ${error.message} (Check console for details)`);
    }
  };

  return (
    <div className="upload-container">
      <h2>🩰 Upload Competition Results</h2>
      
      {/* Authentication Status */}
      <div className="auth-status">
        <strong>Authentication Status:</strong>{" "}
        {authLoading ? (
          "Checking authentication..."
        ) : user ? (
          <span className="status-success">
            ✅ Logged in as {user.email}
          </span>
        ) : (
          <span className="status-error">
            ❌ Not logged in. Please <a href="/login">log in</a> first.
          </span>
        )}
      </div>

      <div className="form-group">
        <label>🏆 Competition Name:</label>
        <input
          type="text"
          placeholder="Optional: override name parsed from raw text"
          value={compName}
          onChange={(e) => setCompName(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label>📄 Paste Raw Results from Contaste (All Rounds):</label>
        <textarea
          rows={12}
          className="code-textarea"
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder="Paste full raw round text here..."
        />
      </div>
      
      <button className="btn btn-primary" onClick={handleConvert}>
        Convert to JSON
      </button>

      <div className="form-group">
        <label>📋 Converted JSON (Review and Edit if Needed):</label>
        <textarea
          placeholder="Converted competition JSON..."
          rows={16}
          className="code-textarea"
          value={jsonInput}
          onChange={(e) => setJsonInput(e.target.value)}
        />
      </div>

      <div className="form-group form-group-inline">
        <label>🧮 Multiplier (default 1):</label>
        <input
          type="number"
          step="0.1"
          min="0.1"
          className="number-input"
          value={multiplier}
          onChange={(e) => setMultiplier(e.target.value)}
        />
      </div>

      <button 
        className={`btn btn-success btn-upload ${!user ? 'btn-disabled' : ''}`}
        onClick={handleUpload}
        disabled={!user}
      >
        Upload to Firebase
      </button>
      
      {uploadStatus && (
        <div className="upload-status">
          {uploadStatus}
        </div>
      )}
    </div>
  );
}
